import React, { useState, useEffect, useMemo } from "react";
import {
	Box,
	Typography,
	Chip,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	TablePagination,
	TextField,
	InputAdornment,
	LinearProgress,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import {
	flexRender,
	getCoreRowModel,
	getSortedRowModel,
	useReactTable,
} from "@tanstack/react-table";
import JobDetailStepper from "./JobDetailStepper.js";
import axios from "axios";

const ellipsisSx = {
	overflow: "hidden",
	textOverflow: "ellipsis",
	whiteSpace: "nowrap",
	fontWeight: 500,
	width: "100%",
};

const getStatusColor = (status) => {
	const normalized = status?.toUpperCase();
	if (normalized === "SUCCESS" || normalized === "COMPLETED") return "success";
	if (normalized === "IN_PROGRESS" || normalized === "PENDING") return "warning";
	if (normalized === "FAILED" || normalized === "ERROR") return "error";
	return "default";
};

const tableColumns = [
	{
		accessorKey: "job_id",
		header: "Job ID",
		cell: (info) => {
			const value = info.getValue();
			return (
				<Typography variant="body2" sx={ellipsisSx} title={value || "-"}>
					{value || "-"}
				</Typography>
			);
		},
	},
	{
		accessorKey: "created_by",
		header: "Created By",
		cell: (info) => {
			const value = info.getValue();
			return (
				<Typography variant="body2" sx={ellipsisSx} title={value || "-"}>
					{value || "-"}
				</Typography>
			);
		},
	},
	{
		accessorKey: "job_type",
		header: "Type",
		cell: (info) => {
			const value = info.getValue();
			return (
				<Typography variant="body2" sx={ellipsisSx} title={value || "-"}>
					{value || "-"}
				</Typography>
			);
		},
	},
	{
		accessorKey: "createdAt",
		header: "Date Executed",
		cell: (info) => {
			const value = info.getValue();
			return (
				<Typography variant="body2" sx={ellipsisSx}>
					{value ? new Date(value).toLocaleString() : "-"}
				</Typography>
			);
		},
	},
	{
		accessorKey: "status",
		header: "Status",
		cell: (info) => {
			const status = info.getValue()?.toUpperCase() || "UNKNOWN";
			return (
				<Chip
					label={status}
					color={getStatusColor(status)}
					size="small"
					variant="outlined"
					sx={{ fontWeight: 600, borderRadius: "6px" }}
				/>
			);
		},
	},
];

const Jobs = () => {
	const [selectedJob, setSelectedJob] = useState(null);
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(false);
	const [sorting, setSorting] = useState([]);
	const [globalFilter, setGlobalFilter] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
	const [totalCount, setTotalCount] = useState(0);

	const columns = useMemo(() => tableColumns, []);

	const table = useReactTable({
		data: rows,
		columns,
		state: { sorting, pagination },
		onSortingChange: setSorting,
		onPaginationChange: setPagination,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		manualPagination: true,
		pageCount: Math.ceil(totalCount / pagination.pageSize) || 1,
	});

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedSearch(globalFilter), 400);
		return () => clearTimeout(timer);
	}, [globalFilter]);

	useEffect(() => {
		const fetchJobs = async () => {
			setLoading(true);
			try {
				const response = await axios.get("/api/pos_reco/get_all_job_logs", {
					params: {
						page: pagination.pageIndex + 1,
						limit: pagination.pageSize,
						search: debouncedSearch,
					},
				});
				const logs = Array.isArray(response.data?.result?.logs) ? response.data.result.logs : [];
				setRows(
					logs.map((job, index) => ({
						...job,
						id: job?.id ?? job?.job_id ?? `job-${index}`,
					}))
				);
				setTotalCount(response.data?.result?.total ?? 0);
			} catch (error) {
				console.error("Failed to fetch jobs:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchJobs();
	}, [pagination.pageIndex, pagination.pageSize, debouncedSearch]);

	if (selectedJob) {
		return (
			<Box
				sx={{
					width: "100%",
					height: "100%",
					display: "flex",
					flexDirection: "column",
					p: { xs: 2, md: 4 },
					boxSizing: "border-box",
					overflow: "hidden",
				}}>
				<Box
					sx={{
						flex: 1,
						minHeight: 0,
						overflow: "auto",
						border: "1px solid",
						borderColor: "divider",
						borderRadius: 2,
						bgcolor: "background.paper",
						p: { xs: 2, md: 3 },
					}}>
					<JobDetailStepper job={selectedJob} onBack={() => setSelectedJob(null)} />
				</Box>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				width: "100%",
				height: "100%",
				display: "flex",
				flexDirection: "column",
				p: { xs: 2, md: 4 },
				boxSizing: "border-box",
				overflow: "hidden",
			}}>
			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					width: "100%",
					display: "flex",
					flexDirection: "column",
					overflow: "hidden",
					border: "1px solid",
					borderColor: "divider",
					borderRadius: 2,
					bgcolor: "background.paper",
				}}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						gap: 1,
						padding: "10px 12px 6px",
						borderBottom: "1px solid",
						borderColor: "divider",
					}}>
					<TextField
						size="small"
						variant="outlined"
						placeholder="Search..."
						value={globalFilter ?? ""}
						onChange={(event) => {
							setGlobalFilter(event.target.value);
							setPagination((prev) => ({ ...prev, pageIndex: 0 }));
						}}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchRoundedIcon fontSize="small" />
								</InputAdornment>
							),
							sx: { borderRadius: 1.5, backgroundColor: "grey.50" },
						}}
					/>
				</Box>
				{loading && <LinearProgress />}
				<TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
					<Table stickyHeader size="small">
						<TableHead>
							{table.getHeaderGroups().map((headerGroup) => (
								<TableRow key={headerGroup.id}>
									{headerGroup.headers.map((header) => {
										const sortDirection = header.column.getIsSorted();
										return (
											<TableCell
												key={header.id}
												align="center"
												sortDirection={sortDirection || false}
												sx={{
													backgroundColor: "grey.50",
													fontWeight: 500,
													color: "text.secondary",
													textTransform: "uppercase",
													fontSize: "0.7rem",
													letterSpacing: "0.4px",
													whiteSpace: "nowrap",
												}}>
												<TableSortLabel
													active={!!sortDirection}
													direction={sortDirection || "asc"}
													onClick={header.column.getToggleSortingHandler()}>
													{flexRender(header.column.columnDef.header, header.getContext())}
												</TableSortLabel>
											</TableCell>
										);
									})}
								</TableRow>
							))}
						</TableHead>
						<TableBody>
							{table.getRowModel().rows.length === 0 ? (
								<TableRow>
									<TableCell colSpan={columns.length} align="center">
										<Typography variant="body2" sx={{ my: 2 }}>
											{loading ? "Loading..." : "No jobs found"}
										</Typography>
									</TableCell>
								</TableRow>
							) : (
								table.getRowModel().rows.map((row) => (
									<TableRow
										key={row.id}
										hover
										onClick={() => setSelectedJob(row.original)}
										sx={{ cursor: "pointer" }}>
										{row.getVisibleCells().map((cell) => (
											<TableCell
												key={cell.id}
												align="center"
												sx={{ fontSize: "0.82rem", whiteSpace: "nowrap" }}>
												{flexRender(cell.column.columnDef.cell, cell.getContext())}
											</TableCell>
										))}
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</TableContainer>
				<TablePagination
					component="div"
					count={totalCount}
					page={pagination.pageIndex}
					onPageChange={(event, newPage) => table.setPageIndex(newPage)}
					rowsPerPage={pagination.pageSize}
					onRowsPerPageChange={(event) => {
						table.setPageSize(Number(event.target.value));
						table.setPageIndex(0);
					}}
					rowsPerPageOptions={[10, 25, 50]}
					sx={{ borderTop: "1px solid", borderColor: "divider", flexShrink: 0 }}
				/>
			</Box>
		</Box>
	);
};

export default Jobs;
